import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CONTACT, INTEREST_OPTIONS } from "@/data/site";
import { api, formatApiErrorDetail } from "@/lib/api";

const empty = { name: "", email: "", phone: "", country: "India", interest: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm(empty);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

  const channels = [
    { icon: Phone, label: "Call us", value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: "Chat with a specialist", href: CONTACT.whatsapp, testid: "contact-whatsapp" },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
  ];

  return (
    <Layout cta={false}>
      <Seo title="Contact Effizent Europe — Porto, Portugal" description="Speak with a Portugal specialist. Call, WhatsApp or email Effizent Europe, or visit us in Porto. Free consultation for your Portugal journey." />
      <PageHero eyebrow="Contact" title="Let's begin the conversation." subtitle="Wherever you are on your journey, a Portugal specialist is ready to help. Free, confidential, no obligation." crumbs={[{ label: "Contact" }]} />

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-20 grid lg:grid-cols-2 gap-14">
        <div>
          <div className="space-y-4">
            {channels.map((c) => (
              <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={c.testid} className="group flex items-center gap-5 rounded-2xl bg-white border border-navy/10 p-6 hover:border-gold/50 hover:shadow-lg transition-all">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-gold group-hover:scale-110 transition-transform"><c.icon className="h-6 w-6" strokeWidth={1.5} /></span>
                <div>
                  <p className="accent-eyebrow text-navy/40" style={{ fontSize: "0.6rem" }}>{c.label}</p>
                  <p className="text-navy font-500 mt-1">{c.value}</p>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {CONTACT.offices.map((o) => (
              <div key={o.country} data-testid={`contact-office-${o.country.toLowerCase()}`} className="rounded-2xl bg-navy text-warm p-6">
                <p className="accent-eyebrow text-gold" style={{ fontSize: "0.6rem" }}>{o.country} Office · {o.city}</p>
                <p className="flex items-start gap-3 mt-3"><MapPin className="h-5 w-5 text-gold shrink-0 mt-0.5" /><span>{o.address}</span></p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-navy/70">
            <a href="/contact.html" data-testid="contact-full-link" className="text-maroon-accent font-600 link-underline">See our full office details</a>
          </p>
        </div>

        <Reveal>
          <div className="rounded-3xl bg-warm border border-navy/10 p-8">
            {done ? (
              <div data-testid="contact-success" className="text-center py-12">
                <Send className="h-12 w-12 text-olive mx-auto" strokeWidth={1.4} />
                <h3 className="font-playfair text-2xl text-navy mt-5">Message received</h3>
                <p className="text-navy/60 mt-2">A specialist will contact you within one business day.</p>
                <button onClick={() => setDone(false)} className="mt-6 rounded-full bg-navy px-7 py-3 text-sm font-600 text-warm">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                <h3 className="font-playfair text-2xl text-navy mb-4">Send us a message</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="contact-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
                  <input data-testid="contact-country" placeholder="Country" value={form.country} onChange={set("country")} className={field} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="contact-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
                  <input data-testid="contact-phone-input" required placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={field} />
                </div>
                <select data-testid="contact-interest" value={form.interest} onChange={set("interest")} className={field}>
                  <option value="">What can we help with?</option>
                  {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <textarea data-testid="contact-message" placeholder="Your message" rows={4} value={form.message} onChange={set("message")} className={field} />
                <button data-testid="contact-submit" disabled={loading} className="w-full rounded-full bg-gold py-3.5 font-600 text-navy hover:bg-gold-soft transition-all disabled:opacity-60">
                  {loading ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </Layout>
  );
}
